import { getStartOfDayTimestamp, PMSTATE, SETTINGSSTATE } from '.'
import { StreamType } from '../../types'
import { Message, PMChatState, PMCollapsedListState } from '../../types/state'
import { PMPlaySoundOnUeopt, SettingsType } from '../constants/enums'
import { playDingSound } from '../mfcLogic/utilities'
import { updatePortalState } from './internal/layout'
import {
    createChatRecord,
    emitPMNotification,
    setPMChatIsTypingState,
    setPMChatLatest,
    setPMUnreadState,
    updateAndEmitMessagesState,
} from './internal/pms'
import { updateRoomCalculations } from './modals/state.floater'
import { pubSubUsers } from './pubSub'

pubSubUsers.on(
    'UPDATE:streamType',
    ({
        previous: previousStreamType,
        current: streamType,
        id,
    }: {
        previous: StreamType
        current: StreamType
        id: number
    }) => {
        if (streamType === 'club') {
            removePMChatState(id, 'clubShow')
        }

        const isPublicShow = !streamType || streamType === 'public'
        const action = isPublicShow ? 'exited' : 'entered'
        const showType = isPublicShow ? previousStreamType : streamType

        if (!showType) {
            return
        }

        const message = {
            text: `You have ${action} the ${showType} show.`,
            success: !isPublicShow,
            warning: isPublicShow,
            timestamp: Date.now(),
            showStatus: true,
            system: true,
        }

        setPMChatUniqueState(id, message, 'showStatus')
    },
)

pubSubUsers.on(
    'UPDATE:isClubShow',
    ({
        previous: wasClubShow,
        current: isClubShow,
        id,
    }: {
        previous: boolean
        current: boolean
        id: number
    }) => {
        if (wasClubShow && !isClubShow && PMSTATE.chats[id]) {
            removePMChatState(id, 'clubShow')
        }
    },
)

pubSubUsers.on(
    'UPDATE:isTypingPM',
    ({ current: isTypingPM, id }: { current: boolean; id: number }) => {
        setPMChatIsTypingState(id, isTypingPM)
    },
)

export const getChatRecord = (id: number): PMChatState => {
    return PMSTATE.chats[id] ?? createChatRecord(id)
}

export const setPMUnreadStateFragment = (id: number, unread: number) => {
    PMSTATE.unreadFragments[id] = unread
    setPMUnreadState(unread)
}

export const setLastKnownScrollEndIndex = (value: number) => {
    PMSTATE.lastKnownScrollEndIndex = value
}

export const setLastKnownScrollOffset = (value: number) => {
    PMSTATE.lastKnownScrollOffset = value
}

/** after this long collapsed, expanding starts the list fresh again */
const PM_LIST_RESTORE_WINDOW_MS = 60 * 1000

/** Collapse the Conversation List, keeping its search, filters, and scroll
 *  so an expand soon after picks up where the user left off */
export const collapsePMList = (
    listState: Pick<
        PMCollapsedListState,
        'showUnreadOnly' | 'sortOrder' | 'searchQuery'
    >,
) => {
    PMSTATE.collapsedList = {
        ...listState,
        scrollOffset: PMSTATE.lastKnownScrollOffset,
        scrollEndIndex: PMSTATE.lastKnownScrollEndIndex,
        collapsedAt: Date.now(),
    }
    updatePortalState({ pmListCollapsed: true })
    updateRoomCalculations()
}

/** Expand the Conversation List. Within the restore window the remounted
 *  list reads `PMSTATE.collapsedList` back; after it, the list starts fresh
 *  at the top, since state that old would surprise the user */
export const expandPMList = () => {
    const { collapsedList } = PMSTATE
    const restore =
        !!collapsedList &&
        Date.now() - collapsedList.collapsedAt < PM_LIST_RESTORE_WINDOW_MS

    // the scroll offset is shared with the Messages page list, so restore
    // it from the snapshot rather than trusting whatever was written since
    PMSTATE.lastKnownScrollOffset = restore ? collapsedList.scrollOffset : 0
    PMSTATE.lastKnownScrollEndIndex = restore ? collapsedList.scrollEndIndex : 0
    if (!restore) PMSTATE.collapsedList = null

    updatePortalState({ pmListCollapsed: false })
    updateRoomCalculations()
}

/** Called once the expanded list has read the snapshot back */
export const clearCollapsedPMList = () => {
    PMSTATE.collapsedList = null
}

const createDateMarker = (timestamp) => {
    const startOfDayTimestamp = getStartOfDayTimestamp(timestamp)
    const date = new Date(startOfDayTimestamp)
    const datetime = date.toDateString() + ' ' + date.toTimeString()

    return {
        id: `marker-${startOfDayTimestamp}`,
        timestamp: startOfDayTimestamp,
        text: datetime,
        marker: true,
    }
}

const setPMChatHistoryTextState = (id, message) => {
    const existingChat = getChatRecord(id)
    const { startOfTodayTimestamp } = PMSTATE

    const { listHasToday, history, latest } = existingChat
    const { timestamp } = message
    const incomingStartOfDayTimestamp = getStartOfDayTimestamp(timestamp)

    if (!history[0] && incomingStartOfDayTimestamp === startOfTodayTimestamp) {
        if (listHasToday) {
            history[0] = latest[0]
            existingChat.latest = latest.slice(1)
        }

        existingChat.listHasToday = true
    }

    const marker = history[0]

    if (!marker || marker?.timestamp !== incomingStartOfDayTimestamp) {
        history.unshift(createDateMarker(timestamp))
    }

    if (!uniqueID[id]) {
        uniqueID[id] = 1
    }

    const messageWithID = { ...message, id: 'id-' + ++uniqueID[id] }

    existingChat.history = [history[0], messageWithID, ...history.slice(1)]
}

export const setPMChatHistoryMessagesState = (id, messages) => {
    const existingChat = getChatRecord(id)

    ;[]
        .concat(messages)
        .forEach((message) => setPMChatHistoryTextState(id, message))

    existingChat.isHistoryUpdated = true

    updateAndEmitMessagesState(id)
}

const uniqueID = {}

export const setPMChatTextState = (id: number, message: Message) => {
    const { warning, system, text, raw, news, info, from, me } = message

    if (!uniqueID[id]) {
        uniqueID[id] = 1
    }

    const messageWithID = { ...message, id: 'id-' + ++uniqueID[id] }

    if (system) {
        const systemMessage: Message = {
            ...messageWithID,
            timestamp: Date.now(),
            pm: true,
            warning,
            system,
            from,
            text,
            info,
        }

        setPMChatLatest(id, systemMessage)
        return
    }

    setPMChatLatest(id, messageWithID)

    const { isOpen } = PMSTATE.chats[id]

    if (!isOpen) {
        emitPMNotification(id, raw)
    }

    if (!news) {
        const activeThisSession = PMSTATE.chats[id].activeThisSession
        const playSoundOn = SETTINGSSTATE[SettingsType.DEFAULT].pmPlaySoundsOn
        if (playSoundOn === PMPlaySoundOnUeopt.none) return
        const shouldPlaySound =
            (!me && playSoundOn === PMPlaySoundOnUeopt.receivedMessage) ||
            playSoundOn === PMPlaySoundOnUeopt.allMessages ||
            (playSoundOn === PMPlaySoundOnUeopt.onlyOnFirstMessage &&
                !activeThisSession &&
                !me)
        const notificationSound =
            SETTINGSSTATE[SettingsType.DEFAULT].pmNotificationSound
        if (shouldPlaySound) playDingSound(notificationSound, !me)
        if (!me) PMSTATE.chats[id].activeThisSession = true
    }
}

const filterOutChatMessage = (id: number, filter: (() => string) | string) => {
    if (typeof filter === 'function') {
        PMSTATE.chats[id].latest = PMSTATE.chats[id].latest.filter(filter)
    }

    if (typeof filter !== 'string') {
        console.warn(
            '[filterOutChatMessage] Filter must be a function or string.',
        )
        return
    }

    PMSTATE.chats[id].latest = PMSTATE.chats[id].latest.filter((data) => {
        const { [filter]: prop } = data
        return !prop
    })
}

export const setPMChatUniqueState = (
    id: number,
    message: Message,
    filter: (() => string) | string,
) => {
    removePMChatState(id, filter)
    setPMChatTextState(id, message)
}

export const setPMChatOnlyIfMissingState = (
    id: number,
    message: Message,
    find: (() => string) | string,
) => {
    const chatRecord = getChatRecord(id)
    if (typeof find === 'string') {
        const found = chatRecord.messages.find((data) => {
            const { [find]: prop } = data
            return prop !== undefined
        })

        if (found) {
            return
        }
    }

    if (typeof find === 'function') {
        const found = chatRecord.messages.find(find)

        if (found) {
            return
        }
    }

    setPMChatTextState(id, message)
}

export const removePMChatState = (
    id: number,
    filter: (() => string) | string,
) => {
    getChatRecord(id) // ensure chat record exists
    filterOutChatMessage(id, filter)

    const {
        chats: { [id]: existingChat },
    } = PMSTATE

    existingChat.isHistoryUpdated = false

    updateAndEmitMessagesState(id)
}

export const setPMChatNewsState = (message: Message) => {
    const { activeID } = PMSTATE

    if (!activeID) {
        return
    }

    setPMChatTextState(activeID, message)
}
